import { skipToken } from '@reduxjs/toolkit/query';
import {
  RrcEp9WasteProfileInspector,
  RrcEp9WasteProfilesPage,
} from './ep9-waste-profiles';
import { CompliancePdfPreview } from './CompliancePdfPreview';
import {
  RrcLocationProfileInspector,
  RrcLocationProfilesPage,
} from './location-profiles';
import { RrcCompanyProfileInspector } from './company-profiles/RrcCompanyProfileInspector';
import { RrcCompanyProfilesPage } from './company-profiles/RrcCompanyProfilesPage';
import type { AdminPageProps } from '../components/AdminPage';
import { OtsActionButton } from '../components/OtsButton';
import { OtsDialog } from '../components/OtsDialog';
import {
  useCallback,
  useEffect,
  useState,
  useRef,
  type ComponentType,
} from 'react';
import {
  extractErrorMessage,
  useGetComplianceProvidersQuery,
  useGetRrcEp10WasteManifestBuildStatusQuery,
  useGetRrcEp10WasteManifestByTtaQuery,
  useGetRrcEp10WasteManifestByPickupQuery,
  useGetRrcEp10WasteManifestVersionsBySubjectQuery,
  useGetRrcEp10WasteManifestDownloadMutation,
  type ComplianceSubject,
  type ComplianceProvider,
  type ComplianceExportFormat,
  type RrcEp10Document,
} from '@ots-frontend/ots-data';
import { OtsThemeRoot, type OtsBrand } from '../theme/OtsTheme';
import { OtsInlineAlert } from '../components/OtsInlineAlert';
import { OtsActionLink } from '../components/OtsButton';
import { ComplianceView } from './ComplianceView';
import { ComplianceDialog } from './ComplianceDialog';
import { RrcEp10ComplianceView } from './RrcEp10ComplianceView';

export type ComplianceProviderViewProps = {
  subject: ComplianceSubject;
  provider: ComplianceProvider;
  compact?: boolean;
  refreshKey: number;
  readOnly?: boolean;
};
export type ComplianceProviderRegistry = Readonly<
  Record<string, ComponentType<ComplianceProviderViewProps>>
>;

export const RrcEp10ComplianceProvider = ({
  subject,
  compact,
  refreshKey,
  readOnly = false,
}: ComplianceProviderViewProps) => {
  const [wasteProfileId, setWasteProfileId] = useState<string>();
  const [managingWaste, setManagingWaste] = useState(false);
  const [locationProfileId, setLocationProfileId] = useState<string>();
  const [managingLocations, setManagingLocations] = useState(false);
  const [companyProfileId, setCompanyProfileId] = useState<string>();
  const [managingCompanies, setManagingCompanies] = useState(false);
  const status = useGetRrcEp10WasteManifestBuildStatusQuery(subject);
  const byTicket = useGetRrcEp10WasteManifestByTtaQuery(
    subject.kind === 'ticket'
      ? { id: subject.id, tenantCode: subject.tenantCode }
      : skipToken
  );
  const byPickup = useGetRrcEp10WasteManifestByPickupQuery(
    subject.kind === 'pickup'
      ? { id: subject.id, tenantCode: subject.tenantCode }
      : skipToken
  );
  const manifest = subject.kind === 'ticket' ? byTicket : byPickup;
  const manifestError =
    manifest.error &&
    'status' in manifest.error &&
    manifest.error.status === 404
      ? undefined
      : manifest.error;
  const versions = useGetRrcEp10WasteManifestVersionsBySubjectQuery(subject);
  const [download, downloadState] =
    useGetRrcEp10WasteManifestDownloadMutation();
  const [downloadError, setDownloadError] = useState<string>();
  const [readyUrl, setReadyUrl] = useState<string>();
  const refresh = useCallback(() => {
    void status.refetch();
    void manifest.refetch();
    void versions.refetch();
  }, [status.refetch, manifest.refetch, versions.refetch]);
  useEffect(() => {
    if (refreshKey) refresh();
  }, [refreshKey, refresh]); // Queries remain mounted when refreshing the workspace.

  const onDownload = async (
    document: RrcEp10Document,
    format: ComplianceExportFormat
  ) => {
    // Reserve the tab during the user gesture, before the signed URL request completes.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    setDownloadError(undefined);
    setReadyUrl(undefined);
    try {
      const url = (
        await download({
          id: document.id,
          tenantCode: subject.tenantCode,
          format,
        }).unwrap()
      ).trim();
      const parsed = new URL(url);
      if (!['https:', 'http:'].includes(parsed.protocol))
        throw new Error('Invalid download URL');
      if (tab && !tab.closed) tab.location.href = parsed.href;
      else setReadyUrl(parsed.href);
    } catch (error) {
      tab?.close();
      setDownloadError(
        extractErrorMessage(
          error,
          'The document could not be downloaded. Please try again.'
        )
      );
    }
  };
  return (
    <>
      {readyUrl && (
        <OtsInlineAlert
          title="Your download is ready"
          action={
            <OtsActionLink
              href={readyUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="compact"
            >
              Open download
            </OtsActionLink>
          }
        >
          Open the document using the link below.
        </OtsInlineAlert>
      )}
      {!subject.tenantCode && (
        <div className="flex justify-end gap-2">
          <OtsActionButton
            size="compact"
            onClick={() => setManagingCompanies(true)}
          >
            Company profiles
          </OtsActionButton>
          <OtsActionButton
            size="compact"
            onClick={() => setManagingLocations(true)}
          >
            Location profiles
          </OtsActionButton>
          <OtsActionButton
            size="compact"
            onClick={() => setManagingWaste(true)}
          >
            EP-9 waste profiles
          </OtsActionButton>
        </div>
      )}
      {managingWaste && (
        <OtsDialog
          open
          divided
          title="EP-9 waste profiles"
          compact={compact}
          onClose={() => setManagingWaste(false)}
        >
          <RrcEp9WasteProfilesPage
            embedded
            compact={compact}
            readOnly={readOnly}
          />
        </OtsDialog>
      )}
      {wasteProfileId && (
        <RrcEp9WasteProfileInspector
          id={wasteProfileId}
          readOnly={readOnly}
          tenantCode={subject.tenantCode}
          compact={compact}
          onClose={() => setWasteProfileId(undefined)}
        />
      )}
      {managingLocations && (
        <OtsDialog
          open
          divided
          title="Location compliance profiles"
          compact={compact}
          onClose={() => setManagingLocations(false)}
        >
          <RrcLocationProfilesPage
            compact={compact}
            embedded
            readOnly={readOnly}
          />
        </OtsDialog>
      )}
      {locationProfileId && (
        <RrcLocationProfileInspector
          id={locationProfileId}
          readOnly={readOnly}
          tenantCode={subject.tenantCode}
          compact={compact}
          onClose={() => setLocationProfileId(undefined)}
          onChanged={refresh}
        />
      )}
      {managingCompanies && (
        <OtsDialog
          open
          title="Company compliance profiles"
          onClose={() => setManagingCompanies(false)}
          divided
          compact={compact}
        >
          <RrcCompanyProfilesPage
            compact={compact}
            embedded
            readOnly={readOnly}
          />
        </OtsDialog>
      )}
      {companyProfileId && (
        <RrcCompanyProfileInspector
          id={companyProfileId}
          readOnly={readOnly}
          tenantCode={subject.tenantCode}
          compact={compact}
          onClose={() => setCompanyProfileId(undefined)}
          onChanged={refresh}
        />
      )}
      <RrcEp10ComplianceView
        onOpenCompanyProfile={setCompanyProfileId}
        onOpenLocationProfile={setLocationProfileId}
        onOpenWasteProfile={setWasteProfileId}
        renderPdf={(document) => (
          <CompliancePdfPreview
            document={{
              program: 'ep10',
              id: document.id,
              tenantCode: subject.tenantCode,
            }}
            label={`EP-10 PDF · ${document.manifestNumber} · Version ${document.version}`}
            compact={compact}
          />
        )}
        compact={compact}
        status={{
          data: status.currentData,
          loading: status.isFetching,
          error: status.error
            ? extractErrorMessage(
                status.error,
                'The build status could not be loaded.'
              )
            : undefined,
        }}
        manifest={{
          data: manifest.currentData,
          loading: manifest.isFetching,
          error: manifestError
            ? extractErrorMessage(
                manifestError,
                'The manifest could not be loaded.'
              )
            : undefined,
        }}
        versions={{
          data: versions.currentData,
          loading: versions.isFetching,
          error: versions.error
            ? extractErrorMessage(
                versions.error,
                'The history could not be loaded.'
              )
            : undefined,
        }}
        onDownload={(document, format) => void onDownload(document, format)}
        downloading={downloadState.isLoading}
        downloadError={downloadError}
        onRetry={refresh}
      />
    </>
  );
};

/** Extend this registry at the host boundary; the shell and other providers stay unchanged. */
export const defaultComplianceProviders: ComplianceProviderRegistry = {
  TEXAS_RRC_EP10: RrcEp10ComplianceProvider,
};

export type ComplianceWorkspaceProps = {
  subject: ComplianceSubject;
  subjectLabel?: string;
  title?: string;
  compact?: boolean;
  brand?: OtsBrand;
  providers?: ComplianceProviderRegistry;
  presentation?: 'embedded' | 'dialog';
  layout?: AdminPageProps['layout'];
  onClose?: () => void;
  onOpenFullPage?: () => void;
  /** Host revision after a ticket edit; refreshes cached data without changing identity. */
  revision?: number;
  /** Prevent writes throughout the workspace, including linked profile inspectors. */
  readOnly?: boolean;
};

const Workspace = ({
  subject,
  subjectLabel,
  title,
  compact,
  brand,
  providers = defaultComplianceProviders,
  presentation = 'embedded',
  layout = 'document',
  onClose,
  onOpenFullPage,
  revision = 0,
  readOnly = false,
}: ComplianceWorkspaceProps) => {
  const query = useGetComplianceProvidersQuery(subject);
  const [refreshKey, setRefreshKey] = useState(0);
  const previousRevision = useRef(revision);
  useEffect(() => {
    if (revision !== previousRevision.current) {
      previousRevision.current = revision;
      void query.refetch();
      setRefreshKey((key) => key + 1);
    }
  }, [revision, query.refetch]);
  const view = {
    title,
    subjectLabel,
    onOpenFullPage,
    compact,
    providers: query.currentData ?? [],
    loading: query.isFetching && !query.currentData,
    refreshing: query.isFetching,
    error: query.error
      ? extractErrorMessage(
          query.error,
          'Applicable compliance programs could not be loaded.'
        )
      : undefined,
    onRefresh: () => {
      void query.refetch();
      setRefreshKey((key) => key + 1);
    },
    renderProvider: (provider: ComplianceProvider) => {
      const Provider =
        providers[provider.provider] ??
        defaultComplianceProviders[provider.provider];
      return Provider ? (
        <Provider
          subject={subject}
          provider={provider}
          compact={compact}
          refreshKey={refreshKey}
          readOnly={readOnly}
        />
      ) : (
        <OtsInlineAlert title="Program details are not available in this version">
          {provider.displayName} is provided by{' '}
          {provider.regulator ?? 'this agency'}. {provider.reason}
        </OtsInlineAlert>
      );
    },
  };
  return presentation === 'dialog' ? (
    <ComplianceDialog
      {...view}
      open
      onClose={onClose ?? (() => undefined)}
      brand={brand}
    />
  ) : (
    <OtsThemeRoot
      brand={brand}
      className={layout === 'fill' ? 'h-full min-h-0' : 'min-h-full'}
    >
      <ComplianceView {...view} layout={layout} />
    </OtsThemeRoot>
  );
};

/** Remount on identity changes so selection and in-flight export state never move between tenants. */
export const ComplianceWorkspace = (props: ComplianceWorkspaceProps) => (
  <Workspace
    key={JSON.stringify([
      props.subject.kind,
      props.subject.tenantCode,
      props.subject.id,
    ])}
    {...props}
  />
);
